import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ShieldAlert } from 'lucide-react';
import api, { errMsg } from '../lib/api';
import { PageHead, Field, Tgl, inp } from './components';
import { PaymentsTable } from './AdminPayments';

const ASSETS = [
  ['btc', 'Bitcoin BTC', 'Bitcoin network (native)'],
  ['usdt_trc20', 'USDT TRC-20', 'TRON network'],
  ['usdt_erc20', 'USDT ERC-20', 'Ethereum network'],
];

export default function AdminCrypto() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get('/admin/settings/payments');
      setCfg(data.crypto || {});
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, []);

  const setAsset = (key, patch) => setCfg((c) => ({ ...c, [key]: { ...(c[key] || {}), ...patch } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/admin/settings/payments', { crypto: cfg });
      toast.success('Crypto settings saved');
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  if (!cfg) return <div className="h-40 bg-white animate-pulse border hairline" />;

  return (
    <div data-testid="admin-crypto">
      <PageHead title="Crypto Payments" sub="BTC and USDT receiving configuration, plus all crypto payment records." />

      <div className="bg-[#FFF7ED] border border-[#FDE68A] p-4 flex gap-3 mb-6" data-testid="crypto-security-note">
        <ShieldAlert className="w-5 h-5 text-[#D97706] shrink-0" />
        <p className="text-[13px] text-[#92400E] leading-relaxed">
          Security: only PUBLIC receiving addresses belong here. Never enter a seed phrase, private key or recovery phrase — this application is designed so private wallet keys never exist in it.
        </p>
      </div>

      <div className="border hairline bg-white p-6 space-y-5 mb-10">
        {ASSETS.map(([key, label, network]) => (
          <div key={key} className="grid grid-cols-1 md:grid-cols-[200px_1fr_140px] gap-3 items-end border-b hairline pb-5 last:border-0 last:pb-0">
            <div>
              <p className="font-display font-bold uppercase">{label}</p>
              <p className="font-mono2 text-[10px] uppercase tracking-wider text-[#8A8F95]">{network}</p>
            </div>
            <Field label="Public receiving address">
              <input data-testid={`crypto-address-${key}`} value={cfg[key]?.address || ''} onChange={(e) => setAsset(key, { address: e.target.value.trim() })}
                placeholder={`${label} public address`} className={`${inp} font-mono2 text-xs`} />
            </Field>
            <Tgl label="Enabled at checkout" checked={cfg[key]?.enabled && !!cfg[key]?.address} onChange={(v) => setAsset(key, { enabled: v })} testid={`crypto-enabled-${key}`} />
          </div>
        ))}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
          <Field label="Payment window (minutes)">
            <input data-testid="crypto-expiry" type="number" min="10" value={cfg.expiry_minutes || 45} onChange={(e) => setCfg((c) => ({ ...c, expiry_minutes: e.target.value }))} className={inp} />
          </Field>
          <Field label="Manual BTC rate (GBP per BTC, optional)">
            <input data-testid="crypto-rate-btc" type="number" step="any" value={cfg.manual_rates?.btc_gbp || ''} onChange={(e) => setCfg((c) => ({ ...c, manual_rates: { ...(c.manual_rates || {}), btc_gbp: e.target.value } }))} className={inp} placeholder="Live rate used if blank" />
          </Field>
          <Field label="Manual USDT rate (GBP per USDT, optional)">
            <input data-testid="crypto-rate-usdt" type="number" step="any" value={cfg.manual_rates?.usdt_gbp || ''} onChange={(e) => setCfg((c) => ({ ...c, manual_rates: { ...(c.manual_rates || {}), usdt_gbp: e.target.value } }))} className={inp} placeholder="Live rate used if blank" />
          </Field>
        </div>
        <button data-testid="crypto-settings-save" onClick={save} disabled={saving}
          className="bg-[#0B0F12] text-white px-6 py-3 font-mono2 text-[10px] uppercase tracking-widest hover:bg-[#1F8A58] transition-colors disabled:opacity-50">
          {saving ? 'Saving…' : 'Save crypto settings'}
        </button>
      </div>

      <p className="font-display font-bold uppercase text-xl mb-4">Crypto payment records</p>
      <PaymentsTable provider="crypto_direct" />
    </div>
  );
}
